import { css, cx } from '@emotion/css';
import { Colors } from '../styles/colors';
import { fieldStyle, h1Style, labelStyle } from '../styles/text';
import { useController, useFormContext } from 'react-hook-form';
import { CloseX, Search } from '../assets/icons';
import { useState } from 'react';
import { ControlHeight, Motion, Radius, Shadow } from '../styles/tokens';

interface ISearchInputProps {
  placeHolder: string;
  hint: string;
  name: string;
}

/**
 * A page title that turns into a search field on focus. The hint is shown as
 * the heading while idle; once the field is focused (or has a value) it
 * becomes a regular search box.
 */
export const SearchInput = (props: ISearchInputProps): JSX.Element => {
  const { control, watch } = useFormContext();
  const [inputFocused, setInputFocused] = useState(false);
  const { placeHolder, hint, name } = props;
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  });
  const userInput = watch(name);
  const isActive = inputFocused || (userInput && userInput !== '');

  return (
    <div className={searchInputStyle(isActive, error)}>
      <input
        {...field}
        className={isActive ? inputActiveStyle : inputIdleStyle}
        onBlur={() => setInputFocused(false)}
        onFocus={() => setInputFocused(true)}
        aria-label={placeHolder}
        type="search"
        autoComplete="off"
      />
      <label className={cx(fieldStyle, inputLabelStyle)}>
        {inputFocused ? placeHolder : hint}
      </label>
      {isActive ? (
        userInput && userInput !== '' ? (
          <button
            type="button"
            className={clearButtonStyle}
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => field.onChange('')}
          >
            <CloseX />
          </button>
        ) : (
          <Search className={suffixSearchIconStyle} />
        )
      ) : (
        <Search className={suffixSearchIconStyle} />
      )}

      {error && (
        <span className={cx(labelStyle, errorStyle)}>{error.message}</span>
      )}
    </div>
  );
};

const searchInputStyle = (isActive: boolean, error: any) => css`
  width: 100%;
  height: 5.25rem;
  position: relative;
  display: flex;
  flex-direction: column;

  &:focus-within label {
    ${labelStyle}
    transform: translate(1.125rem, 0.75rem) scale(1);
    color: ${Colors.CAPRI_BLUE};
  }
  label {
    ${isActive &&
    css`
      ${labelStyle}
      transform: translate(1.125rem, 0.75rem) scale(1);
      color: ${Colors.TEXT_SECONDARY};
    `}
  }
  input {
    ${error &&
    css`
      border-color: ${Colors.ERROR_DARK};
    `}
  }

  &:focus-within input {
    border-color: ${Colors.CAPRI_BLUE};
    box-shadow: ${Shadow.focus};
  }
`;

const inputLabelStyle = css`
  ${h1Style}
  position: absolute;
  pointer-events: none;
  transform: translate(0rem, 0.75rem) scale(1);
  transform-origin: top left;
  transition:
    transform 200ms ease-out 0ms,
    font-size 200ms ease-out 0ms,
    color 200ms ease-out 0ms;
  text-transform: capitalize;
`;

const baseInputStyle = css`
  height: ${ControlHeight.input};
  padding: 28px 3.25rem 12px 16px;
  position: absolute;
  background: none;
  outline: none;
  width: 100%;
  -webkit-appearance: none;
  -moz-appearance: textfield;
  border-radius: ${Radius.md};
  transition:
    border-color ${Motion.fast},
    box-shadow ${Motion.fast},
    background-color ${Motion.fast};

  &::-webkit-search-cancel-button,
  &::-webkit-search-decoration {
    -webkit-appearance: none;
  }
`;

const inputIdleStyle = css`
  ${baseInputStyle}
  border: 0.125rem solid transparent;
  cursor: text;
`;

const inputActiveStyle = css`
  ${baseInputStyle}
  border: 0.125rem solid ${Colors.CAPRI_BLUE};
  background: ${Colors.WHITE};
`;

const errorStyle = css`
  color: ${Colors.ERROR_DARK};
  transform: translate(1.125rem, 62px) scale(1);
  transform-origin: top left;
  pointer-events: none;
  &:first-letter {
    text-transform: capitalize;
  }
`;

const suffixSearchIconStyle = css`
  position: absolute;
  top: 0px;
  right: 0px;
  transform: translate(-1rem, 1rem);
  pointer-events: none;
`;

const clearButtonStyle = css`
  appearance: none;
  background: none;
  border: 0;
  position: absolute;
  top: 0;
  right: 0;
  width: 3.5rem;
  height: ${ControlHeight.input};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: ${Radius.md};
`;
